import type { MedplumClient } from '@medplum/core'
import { config } from '@/config'

export interface AddressInput {
  street: string
  city: string
  state: string
  zip: string
}

export interface GeocodeResult {
  lat: number
  lon: number
}

const SECONDARY_UNIT_PATTERN =
  /^(.*?)(?:\s*,\s*|\s+)(?:apt|apartment|unit|ste|suite|fl|floor|bldg|building|rm|room|dept)\.?\s+[a-z0-9-]+(?:\s+[a-z0-9-]+)*\s*$/i
const HASH_UNIT_PATTERN = /^(.*?)\s+#\s*[a-z0-9-]+(?:\s+[a-z0-9-]+)*\s*$/i

export function sanitizeStreetForGeocoding(street: string): string {
  const normalized = street.trim().replace(/\s+/g, ' ')
  if (!normalized) return normalized

  for (const pattern of [SECONDARY_UNIT_PATTERN, HASH_UNIT_PATTERN]) {
    const match = normalized.match(pattern)
    const streetOnly = match?.[1]?.trim().replace(/[,\s]+$/, '')
    if (streetOnly) {
      return streetOnly
    }
  }

  return normalized
}

/**
 * Calls the middleware /order/geocode endpoint. Returns raw coordinates so
 * callers can format them for whatever shape they need (FHIR geolocation
 * extension, Location.position, API payload, etc). Returns undefined if
 * geocoding failed, did not find coordinates, or the middleware URL is not
 * configured. Never throws.
 */
export async function geocodeAddress(
  medplum: MedplumClient,
  address: AddressInput,
): Promise<GeocodeResult | undefined> {
  const baseUrl = (config.middlewareUrl || '').replace(/\/+$/, '')
  if (!baseUrl) return undefined

  const token = medplum.getAccessToken()
  const fullAddress = [
    sanitizeStreetForGeocoding(address.street),
    address.city,
    address.state,
    address.zip,
  ]
    .filter(Boolean)
    .join(', ')

  try {
    const resp = await fetch(`${baseUrl}/order/geocode?q=${encodeURIComponent(fullAddress)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (!resp.ok) return undefined
    const coords = (await resp.json()) as { lat: number; lon: number } | null
    if (coords?.lat == null || coords?.lon == null) return undefined
    return { lat: coords.lat, lon: coords.lon }
  } catch {
    return undefined
  }
}
